/** I filtri attivi su una lista, resi visibili e rimovibili.
 *
 * Nasce da un difetto preciso: cliccando un numero nella pagina statistiche si
 * arriva alla lista già filtrata, ma **senza vedere quale filtro sia stato
 * applicato**. La lista sembra misteriosamente più corta e non c'è modo di capire
 * perché né di tornare indietro.
 *
 * Tre cause, tutte verificate nel codice il 2026-08-04:
 *
 * 1. Un banner esisteva già, ma con `className="alert"` **senza variante**. In
 *    `indaco.css` la classe base `.alert` non dichiara alcun `background` e ha
 *    `border: 1px solid transparent`: il risultato era una riga di testo sul fondo
 *    della pagina, praticamente invisibile. Le varianti colorate erano lì, pronte
 *    e non usate.
 * 2. Copriva 3 filtri su 5: `had_corrections` e `discard_type` non comparivano.
 * 3. Il filtro si poteva togliere solo tutto insieme.
 *
 * Il componente è generico e la lista dichiara i propri filtri: aggiungerne uno è
 * una voce nell'array, non un ramo qui dentro. Le liste che oggi non ricevono
 * drill-down (BSC, Rinascente) lo eredidano quando serviranno, senza modifiche.
 */

export interface FiltroAttivo {
  /** Query param da azzerare per rimuoverlo. */
  chiave: string;
  /** Cosa restringe, in parole: "Famiglia di scarto". */
  etichetta: string;
  /** Il valore, già reso leggibile dal chiamante. */
  valore: string;
  /** Filtro che isola un fallimento del sistema: colorato come tale. */
  grave?: boolean;
}

export function ActiveFiltersBar({
  filtri,
  onRemove,
}: {
  filtri: FiltroAttivo[];
  /** Riceve le chiavi da azzerare: una sola per la × di una chip, tutte per "Rimuovi tutti". */
  onRemove: (chiavi: string[]) => void;
}) {
  if (!filtri.length) return null;
  const grave = filtri.some((f) => f.grave);

  return (
    <div
      // La variante è ciò che rende il banner visibile: `alert` da sola non ha
      // sfondo. `alert-danger` quando il filtro isola un errore nostro, così il
      // colore porta un'informazione e non è decorazione.
      className={`alert ${grave ? "alert-danger" : "alert-info"}`}
      style={{
        marginBottom: "var(--space-3)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        flexWrap: "wrap",
      }}
      role="status"
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0 }} aria-hidden>
        filter_alt
      </span>
      <span className="text-sm fw-medium" style={{ flexShrink: 0 }}>
        {filtri.length === 1 ? "Filtro attivo:" : "Filtri attivi:"}
      </span>

      <span style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", minWidth: 0 }}>
        {filtri.map((f) => (
          <span
            key={f.chiave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--space-1)",
              maxWidth: 340,
              padding: "1px var(--space-1) 1px var(--space-2)",
              borderRadius: 10,
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-semibold)",
              background: "var(--indaco-white)",
              border: "1px solid var(--border-color)",
            }}
            title={`${f.etichetta}: ${f.valore}`}
          >
            <span className="text-muted" style={{ fontWeight: "var(--font-weight-regular)" }}>
              {f.etichetta}:
            </span>
            <span className="truncate">{f.valore}</span>
            {/* Rimozione singola: prima si poteva solo azzerare tutto, quindi per
                togliere un filtro se ne perdevano anche gli altri. */}
            <button
              type="button"
              onClick={() => onRemove([f.chiave])}
              aria-label={`Rimuovi il filtro ${f.etichetta}`}
              title={`Rimuovi il filtro ${f.etichetta}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--indaco-gray-600)",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }} aria-hidden>
                close
              </span>
            </button>
          </span>
        ))}
      </span>

      {filtri.length > 1 && (
        <button
          type="button"
          className="btn btn-outline btn-xs"
          style={{ marginLeft: "auto", flexShrink: 0 }}
          onClick={() => onRemove(filtri.map((f) => f.chiave))}
        >
          Rimuovi tutti
        </button>
      )}
    </div>
  );
}
